import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { applyBlockReplacementsToZip } from "@/utils/htmlToWordXml";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Word quebra tags com "_" em vários <w:t>; junta antes do docxtemplater. */
export function collapseSplitPlaceholders(xml) {
  let result = "";
  let offset = 0;
  const runBreak =
    /<\/w:t>\s*(?:<\/w:r>\s*)?(?:<w:r[^>]*>\s*(?:<w:rPr>[\s\S]*?<\/w:rPr>\s*)?)?<w:t[^>]*>\s*/g;

  let start = xml.indexOf("{{", offset);
  while (start !== -1) {
    result += xml.slice(offset, start);
    const end = xml.indexOf("}}", start + 2);
    if (end === -1) {
      result += xml.slice(start);
      return result;
    }
    let chunk = xml.slice(start, end + 2);
    chunk = chunk.replace(runBreak, "");
    result += chunk;
    offset = end + 2;
    start = xml.indexOf("{{", offset);
  }
  result += xml.slice(offset);

  return result;
}

const DOCX_XML_PARTS =
  /^word\/(document|header\d+|footer\d+|footnotes|endnotes)\.xml$/;

function normalizeDocxZip(zip) {
  Object.keys(zip.files).forEach((name) => {
    if (!DOCX_XML_PARTS.test(name)) return;
    const file = zip.files[name];
    if (!file || file.dir) return;
    zip.file(name, collapseSplitPlaceholders(file.asText()));
  });
}

/** Placeholders no Word costumam vir como `{{ $nome }}` (com espaços); a API envia `$nome`. */
function memorialPlaceholderParser(tag) {
  const key = String(tag).trim();
  return {
    get(scope) {
      if (key === ".") {
        return scope;
      }
      if (!scope || !Object.prototype.hasOwnProperty.call(scope, key)) {
        return "";
      }
      const value = scope[key];
      return value == null ? "" : value;
    },
  };
}

/**
 * Preenche o .docx no navegador (docxtemplater), mantendo fonte, margens e estilos do modelo.
 * Apenas o texto das variáveis {{ $chave }} é substituído.
 *
 * @param {object} [options]
 * @param {Record<string, string>} [options.blockFields] placeholders de bloco (ex.: $kit com tabela Word)
 */
export function fillMemorialDocx(arrayBuffer, fields, options = {}) {
  const zip = new PizZip(arrayBuffer);
  normalizeDocxZip(zip);

  const blockFields = options.blockFields || {};
  if (Object.keys(blockFields).length) {
    applyBlockReplacementsToZip(zip, blockFields);
  }

  const fieldsForTemplate = { ...fields };
  Object.keys(blockFields).forEach((key) => {
    if (fieldsForTemplate[key] !== undefined) {
      fieldsForTemplate[key] = "";
    }
  });

  const doc = new Docxtemplater(zip, {
    paragraphLoop: false,
    linebreaks: true,
    delimiters: { start: "{{", end: "}}" },
    parser: memorialPlaceholderParser,
    nullGetter: () => "",
  });
  doc.setData(fieldsForTemplate);
  try {
    doc.render();
  } catch (e) {
    const msg =
      (e.properties && e.properties.explanation) || e.message || String(e);
    const err = new Error(
      "Falha ao preencher o Word: " +
        msg +
        ". Digite cada variável de uma vez no Word (evite que o Word quebre {{ $nome }} no meio do texto)."
    );
    err.cause = e;
    throw err;
  }
  return doc.getZip().generate({
    type: "blob",
    mimeType: DOCX_MIME,
    compression: "DEFLATE",
  });
}

export function downloadMemorialDocxFile(blob, filename) {
  const name =
    filename && String(filename).trim() ? filename : "memorial.docx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  a.click();
  URL.revokeObjectURL(url);
}

function parseApiErrorBody(text) {
  if (!text) return text;
  try {
    const json = JSON.parse(text);
    if (typeof json === "string") return json;
    return json.message || json.error || text;
  } catch {
    return text;
  }
}

/**
 * Baixa o modelo .docx original do servidor e preenche no navegador (preserva formatação).
 *
 * @param {string} baseUrl  ex. axios.defaults.baseURL (…/api/v1)
 * @param {string|number} projectId
 * @param {string} token  Bearer
 */
export async function generateMemorialDocxFromServer({
  baseUrl,
  projectId,
  token,
}) {
  const base = (baseUrl || "").replace(/\/?$/, "/");
  const dataUrl = base + "memorial/" + projectId + "/data";
  const templateUrl = base + "memorial/" + projectId + "/template";

  const authHeaders = {
    Accept: "application/json",
    Authorization: "Bearer " + token,
  };

  const dataRes = await fetch(dataUrl, { headers: authHeaders });
  if (!dataRes.ok) {
    const errText = await dataRes.text();
    throw new Error(
      "Não foi possível obter os dados do memorial: " +
        (parseApiErrorBody(errText) || dataRes.status)
    );
  }

  const json = await dataRes.json();
  const fields = json.fields;
  if (!fields || typeof fields !== "object") {
    throw new Error("Resposta inválida da API (faltou fields).");
  }
  if (json.hasTemplate === false) {
    throw new Error(
      "Modelo de memorial não configurado. Envie o .docx em Configurações → Memorial Descritivo."
    );
  }

  const tplRes = await fetch(templateUrl, {
    headers: { Authorization: "Bearer " + token },
  });
  if (!tplRes.ok) {
    const errText = await tplRes.text();
    throw new Error(
      "Não foi possível carregar o modelo .docx: " +
        (parseApiErrorBody(errText) || tplRes.status)
    );
  }

  const buf = await tplRes.arrayBuffer();
  return fillMemorialDocx(buf, fields);
}
